import { apiClient } from '../api';
import { Path } from '../api/backendUrl';

/**
 * Fetch business details by business slug.
 * Multi-business support (Phase 1) — per approved_architecture.md §5.5.
 *
 * During backend transition, falls back to fetching the provider by slug
 * and extracting the matching business from the businesses[] array.
 */
export const getBusinessBySlug = async (businessSlug: string) => {
  try {
    const url =
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.getBusinessBySlug}`.replace(
        ':businessSlug',
        businessSlug
      );
    const res = await apiClient.get(url);
    if (res?.data?.success) return res.data.business ?? res.data;
    return res.data;
  } catch (error: any) {
    const errorCode = error.response?.data?.code;
    console.log('ERROR get business by slug: ', error);
    if (errorCode === 404) {
      // Fallback: try provider-by-slug and extract matching business
      try {
        const { getProviderDetailsByProvider } = await import(
          './ProviderService'
        );
        const provider = await getProviderDetailsByProvider(businessSlug);
        const business = provider?.businesses?.find(
          (b: any) => b.businessSlug === businessSlug
        );
        if (business) return business;
      } catch (_fallbackErr) {
        // fallback failed — let the original error propagate
      }
      window.location.replace('/');
      return;
    }
    throw error;
  }
};

/**
 * Fetch public services for a specific business.
 * Falls back to provider-level query if businessId filtering isn't available yet.
 */
export const getPublicServicesByBusiness = async (params: {
  businessId: string;
  providerId?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const queryParts: string[] = [];
    if (params.businessId) queryParts.push(`businessId=${params.businessId}`);
    if (params.page) queryParts.push(`page=${params.page}`);
    if (params.limit) queryParts.push(`limit=${params.limit}`);

    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const res = await apiClient.get(
      `${import.meta.env.VITE_APP_BACKEND_SERVICE}/${Path.getPublicServicesByBusiness}${queryString}`
    );
    return res.data;
  } catch (error: unknown) {
    console.log('ERROR get public services by business: ', error);
    throw error;
  }
};

/**
 * Fetch public stats for a specific business (completed jobs, etc.).
 */
export const getBusinessPublicStats = async (businessId: string) => {
  try {
    const url =
      `${import.meta.env.VITE_APP_BACKEND_SERVICE}${Path.businessPublicStats}`.replace(
        ':businessId',
        businessId
      );
    const res = await apiClient.get(url);
    return res.data;
  } catch (error: unknown) {
    console.log('ERROR get business public stats: ', error);
    throw error;
  }
};
